import {
  NewButton,
  NewButtonSize,
  NewButtonVariant,
  Pill,
  TooltipTrigger,
} from "@visiblethread/react-components";
import clsx from "clsx";
import Flex, { GAP_CLASS } from "components/Flex";
import { ALIGN_CLASS } from "components/Flex/Flex";
import useContainerSize from "hooks/useContainerSize";
import { EditIcon } from "lucide-react";
import PropTypes, { string } from "prop-types";
import { useMemo } from "react";
import { formatDate } from "utils";
import { getStyles } from "utils/getStyles";
import { getVisibleItems } from "utils/getVisibleItems";

import classes from "./SharedByCell.module.scss";

const SharedByCell = ({
  owner,
  sharedOn,
  sharedWith = [],
  viewMode = true,
  isActive = true,
  isShared = false,
  isAll = false,
  isSystemPrompt = false,
  className,
  labelKey = "label",
  onClick,
}) => {
  const isEditable = !viewMode && !isSystemPrompt;

  const styles = getStyles();

  const {
    ref: pillContainerRef,
    width: maxPillContainerWidth,
    height: maxPillContainerHeight,
  } = useContainerSize();

  const sharedWithLabels = useMemo(
    () => sharedWith.map((item) => item[labelKey]),
    [sharedWith]
  );

  const maxVisibleItems = getVisibleItems(
    sharedWithLabels,
    maxPillContainerWidth,
    maxPillContainerHeight
  );

  const visibleData = sharedWith.slice(0, maxVisibleItems);
  const invisibleData = sharedWith.slice(maxVisibleItems);
  const remainingCount = sharedWith.length - maxVisibleItems;

  return (
    <TooltipTrigger
      content="This is a global system prompt. It's shared with everyone and can't be edited."
      delay={100}
      isEnabled={isSystemPrompt && !viewMode}
      maxWidth="400px"
      maxHeight="400px"
    >
      <Flex
        justifyContent="space-between"
        alignItems={ALIGN_CLASS.center}
        gap={GAP_CLASS.GAP_100}
        className={clsx(
          classes.shared_cell,
          isEditable && classes["shared_cell--editing"],
          className
        )}
        onClick={() => {
          isEditable && onClick?.();
        }}
        role={isEditable ? "button" : undefined}
        tabIndex={isEditable ? 0 : -1}
      >
        <div className={classes.shared_cell__content}>
          {isShared && (
            <>
              <div className={classes.shared_cell__content__owner}>
                <div className={classes.shared_cell__content__owner__label}>
                  Shared by:
                </div>
                <Pill
                  className={classes.shared_cell__content__owner__pill}
                  label={owner}
                  color={isActive ? styles.lightOrange : styles.middleGray}
                />
              </div>

              {sharedWith.length > 0 && (
                <>
                  <div className={classes.shared_cell__content__shared_on}>
                    on {formatDate(sharedOn)} with:
                  </div>

                  <div className={classes.shared_cell__content__shared_with}>
                    <div
                      ref={pillContainerRef}
                      className={
                        classes.shared_cell__content__shared_with__pills
                      }
                    >
                      {isAll ? (
                        <Pill
                          key="all"
                          label="All"
                          color={
                            isActive ? styles.lightPurple : styles.middleGray
                          }
                        />
                      ) : (
                        <>
                          {visibleData.map((item) => (
                            <Pill
                              key={item.id}
                              label={item[labelKey]}
                              color={
                                isActive
                                  ? styles.lightPurple
                                  : styles.middleGray
                              }
                            />
                          ))}

                          {remainingCount > 0 && (
                            <TooltipTrigger
                              content={
                                <div
                                  className={
                                    classes[
                                      "shared_cell__content__shared_with__pills__hidden"
                                    ]
                                  }
                                >
                                  {invisibleData.map((item) => (
                                    <span key={item.id}>{item[labelKey]}</span>
                                  ))}
                                </div>
                              }
                              delay={100}
                              maxWidth="400px"
                              maxHeight="400px"
                              classes={
                                classes.shared_cell__content__shared_with__pills__tooltip_trigger
                              }
                            >
                              <Pill
                                label={`+${remainingCount} more`}
                                color={
                                  isActive
                                    ? styles.lightPurple
                                    : styles.middleGray
                                }
                              />
                            </TooltipTrigger>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {isEditable && isShared && (
          <NewButton
            size={NewButtonSize.sm}
            variant={NewButtonVariant.parent}
            data-show-on-row-hover
            data-testid="edit-icon-button"
            leftIcon={<EditIcon size={16} />}
          />
        )}
      </Flex>
    </TooltipTrigger>
  );
};

export default SharedByCell;

SharedByCell.propTypes = {
  owner: string,
  sharedOn: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  sharedWith: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ),
  viewMode: PropTypes.bool,
  isActive: PropTypes.bool,
  isAll: PropTypes.bool,
  isShared: PropTypes.bool,
  isSystemPrompt: PropTypes.bool,
  className: PropTypes.string,
  labelKey: PropTypes.string,
  onClick: PropTypes.func,
};
